<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.settings") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="saving || loading" @click="save">{{ $t("pos.common.save") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<div class="row" v-loading="loading">
				<!-- phase 3 R-PAY: the receiving account replaces the P1 PromptPay card (B48) -->
				<div class="col-12">
					<card>
						<div slot="header" class="d-flex align-items-center justify-content-between">
							<h4 class="mb-0">{{ $t("pos.payment_account.card") }}</h4>
							<base-button size="sm" type="primary" :disabled="accountSaving || loading" @click="saveAccount">{{ $t("pos.payment_account.save") }}</base-button>
						</div>
						<pos-payment-account-form v-model="account" :bad="accountBad"></pos-payment-account-form>
					</card>
				</div>
				<div class="col-lg-6">
					<card>
						<h4 slot="header" class="mb-0">{{ $t("pos.settings.cap_card") }}</h4>
						<div class="row">
							<div class="col-6 form-group">
								<label class="form-control-label d-block">{{ $t("pos.settings.cap_pct") }}</label>
								<el-input-number v-model="setting.default_discount_cap_pct" :min="0" :max="100" :precision="2" :controls="false" class="w-100"></el-input-number>
							</div>
							<div class="col-6 form-group">
								<label class="form-control-label d-block">{{ $t("pos.settings.cap_amount") }}</label>
								<el-input-number v-model="setting.default_discount_cap_amount" :min="0" :precision="2" :controls="false" class="w-100"></el-input-number>
							</div>
						</div>
						<small class="text-muted">{{ $t("pos.event.cap_hint") }}</small>
					</card>
				</div>
				<div class="col-12">
					<card>
						<h4 slot="header" class="mb-0">{{ $t("pos.settings.receipt_card") }}</h4>
						<pos-receipt-form v-model="receipt"></pos-receipt-form>
					</card>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { InputNumber } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import posPageMixin from "./posPageMixin";
import PosInstallAlert from "./PosInstallAlert.vue";
import PosReceiptForm from "./PosReceiptForm.vue";
import PosPaymentAccountForm from "./PosPaymentAccountForm.vue";
import { paymentAccountForm, paymentAccountBody, validatePaymentAccount } from "./posPaymentAccount";

const numOrNull = (v) => (v === undefined || v === null || v === "" || isNaN(Number(v)) ? null : Number(v));

export default {
	name: "PosSettings",
	mixins: [posPageMixin],
	components: { RouteBreadCrumb, PosInstallAlert, PosReceiptForm, PosPaymentAccountForm, [InputNumber.name]: InputNumber },
	data() {
		return {
			saving: false,
			account: paymentAccountForm(null),
			accountHasQr: false,
			accountBad: [],
			accountSaving: false,
			setting: { default_discount_cap_pct: undefined, default_discount_cap_amount: undefined },
			receipt: { show_customer_address: true, footer_text: "ขอบคุณที่ใช้บริการ" }
		};
	},
	mounted() {
		this.load();
	},
	methods: {
		async load() {
			this.loading = true;
			try {
				const [s, r, a] = await Promise.all([PosService.PosSettingGet(), PosService.PosReceiptSettingGet(), PosService.paymentAccountGet()]);
				const ad = (a.data && a.data.Datum) || null;
				this.account = paymentAccountForm(ad && ad.mode ? ad : null);
				this.accountHasQr = !!(ad && ad.qr_image_data_url);
				this.accountBad = [];
				const sd = (s.data && s.data.Datum) || {};
				this.setting = {
					default_discount_cap_pct: sd.default_discount_cap_pct === null ? undefined : sd.default_discount_cap_pct,
					default_discount_cap_amount: sd.default_discount_cap_amount === null ? undefined : sd.default_discount_cap_amount
				};
				const rd = (r.data && r.data.Datum) || {};
				this.receipt = {
					logo_data_url: rd.logo_data_url || "",
					shop_name: rd.shop_name || "",
					address: rd.address || "",
					phone: rd.phone || "",
					header_note: rd.header_note || "",
					footer_text: rd.footer_text || "",
					footer_qr_text: rd.footer_qr_text || "",
					footer_qr_caption: rd.footer_qr_caption || "",
					show_customer_address: rd.show_customer_address !== false
				};
				this.notInstalled = false;
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		async saveAccount() {
			this.accountBad = validatePaymentAccount(this.account, this.accountHasQr);
			if (this.accountBad.length) return;
			this.accountSaving = true;
			try {
				await PosService.paymentAccountPut(paymentAccountBody(this.account, false));
				this.posToast("pos.common.saved");
				this.load();
			} catch (err) {
				const r = err && err.response;
				if (r && r.data && r.data.Msg === "pos_payment_account_invalid") this.accountBad = String(r.data.Cause || "").split(",");
				if (r && r.data && r.data.Msg === "pos_qr_image_invalid") this.accountBad = ["qr_image_data_url"];
				this.posError(err);
			} finally {
				this.accountSaving = false;
			}
		},
		async save() {
			this.saving = true;
			try {
				await PosService.PosSettingPut({
					default_discount_cap_pct: numOrNull(this.setting.default_discount_cap_pct),
					default_discount_cap_amount: numOrNull(this.setting.default_discount_cap_amount)
				});
				await PosService.PosReceiptSettingPut(this.receipt);
				this.posToast("pos.common.saved");
				this.load();
			} catch (err) {
				this.posError(err);
			} finally {
				this.saving = false;
			}
		}
	}
};
</script>
